import {
  DashboardViewRegion,
  DashboardViewRegionsFlatListMap,
  DashboardViewRegionsParentMap,
} from '../types/dashboard-view-regions.types';

/**
 * Creates a parent map for the dashboard view regions.
 * The result is an array of relations between the view region id and its parent id.
 */
export const dashboardViewRegionsCreateParentMap = (viewRegions: DashboardViewRegion[]): DashboardViewRegionsParentMap => {
  // Initialize stack with the root view regions.
  const stackToProceed = [...viewRegions];

  // Initialize a parent map as an JS Map.
  const parentMap: Map<DashboardViewRegion['id'], DashboardViewRegion['id']> = new Map();

  // Iterate over the stack.
  while (stackToProceed.length > 0) {
    // Extract the last element from the stack.
    const viewRegion = stackToProceed.pop();

    // Push the child regions of the extracted element to the stack and add them to the parent map.
    for (const child of viewRegion.subregions) {
      stackToProceed.push(child);
      parentMap.set(child.id, viewRegion.id);
    }
  }

  // Return the parent map.
  return parentMap;
};

/**
 * Defines the root parent id for the dashboard view region.
 */
export const dashboardViewRegionsDefineRootParentId = (
  viewRegionId: DashboardViewRegion['id'],
  viewRegionsParentMap: DashboardViewRegionsParentMap
): DashboardViewRegion['id'] => {
  let currentViewRegionId = viewRegionId;
  let nearestParentViewRegionId = viewRegionsParentMap.get(currentViewRegionId);

  // Iterate over the parent map until the root parent is found.
  while (nearestParentViewRegionId) {
    currentViewRegionId = nearestParentViewRegionId;
    nearestParentViewRegionId = viewRegionsParentMap.get(nearestParentViewRegionId);
  }

  return currentViewRegionId;
};

/**
 *
 */
export const dashboardViewRegionsGetChainedIds = (
  viewRegionId: DashboardViewRegion['id'],
  viewRegionsParentMap: DashboardViewRegionsParentMap
): DashboardViewRegion['id'][] => {
  const chainedIds: DashboardViewRegion['id'][] = [viewRegionId];
  let currentViewRegionId = viewRegionId;
  let nearestParentViewRegionId = viewRegionsParentMap.get(currentViewRegionId);

  // Iterate over the parent map until the root parent is found.
  while (nearestParentViewRegionId) {
    chainedIds.push(nearestParentViewRegionId);
    currentViewRegionId = nearestParentViewRegionId;
    nearestParentViewRegionId = viewRegionsParentMap.get(nearestParentViewRegionId);
  }

  return chainedIds.reverse();
};

/**
 * Form a flat list map for the dashboard view regions.
 * The result is an array of relations between the view region id and the view region.
 */
export const dashboardViewRegionsFormFlatListMap = (viewRegions: DashboardViewRegion[]): DashboardViewRegionsFlatListMap => {
  // Initialize stack with the root view regions.
  const stackToProceed = [...viewRegions];

  // Initialize a parent map as an JS Map.
  const flatListMap: Map<DashboardViewRegion['id'], DashboardViewRegion> = new Map();

  // Iterate over the stack.
  while (stackToProceed.length > 0) {
    // Extract the last element from the stack.
    const viewRegion = stackToProceed.pop();

    // Add the view region to the flat list map.
    flatListMap.set(viewRegion.id, viewRegion);

    // Push the child regions of the extracted element to the stack.
    for (const child of viewRegion.subregions) {
      stackToProceed.push(child);
    }
  }

  // Return the parent map.
  return flatListMap;
};
